import { AsyncPipe, NgTemplateOutlet } from "@angular/common";
import {
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { NgbTypeahead } from "@ng-bootstrap/ng-bootstrap";
import {
    BehaviorSubject,
    Observable,
    PartialObserver,
    Subject,
    asyncScheduler,
    firstValueFrom,
    merge
} from "rxjs";
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    tap
} from "rxjs/operators";
import { MinaMaskinlagDialogService } from "~app/features/planera/mina-maskinlag/mina-maskinlag-dialog.service";
import { MinaProduktionsmallarDialogService } from "~app/features/planera/produktionsmallar/services/mina-produktionsmallar-dialog.service";
import { MinaForkalkylsmallarDialogService } from "~app/features/virkeskop/forkalkylsmallar/mina-forkalkylsmallar-dialog.service";
import { BusySpinnerComponent } from "../busy/busy-spinner/busy-spinner.component";
import { ISearchResponseRow, QueryResultTypes } from "./quick-search.models";
import { QuickSearchService } from "./quick-search.service";
import { RendererResolverDirective } from "./search-result-renderers/renderer-resolver.directive";

@Component({
    selector: "rask-quick-search",
    templateUrl: "./quick-search.component.html",
    styleUrls: ["./quick-search.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        NgbTypeahead,
        BusySpinnerComponent,
        RendererResolverDirective,
        NgTemplateOutlet,
        AsyncPipe
    ]
})
export class QuickSearchComponent implements OnInit, OnDestroy {
    activeSearchResults = new BehaviorSubject<ISearchResponseRow[]>([]);
    clearSrc = new Subject<void>();
    data: ISearchResponseRow[] = [];
    searchComplete$ = new BehaviorSubject<boolean>(true);
    @ViewChild("quickSearchInput") searchElement: ElementRef;
    searchString: string;
    @ViewChild("typeaheadInstance") typeaheadInstance: NgbTypeahead;

    private readonly debounceTime = 200;
    private readonly minaForkalkylsmallarDialogService = inject(MinaForkalkylsmallarDialogService);
    private readonly minaMaskinlagDialogService = inject(MinaMaskinlagDialogService);
    private readonly minaProduktionsmallarDialogService = inject(MinaProduktionsmallarDialogService);
    private readonly router = inject(Router);
    private readonly searchService = inject(QuickSearchService);
    private readonly typeTooltipMap: Map<string, string>;
    private readonly zone = inject(NgZone);
    private readonly extendedSearchSubject = new Subject<string>();

    constructor() {
        this.typeTooltipMap = new Map([
            [
                QueryResultTypes.Arbetsorder,
                "Arbetsorder"
            ],
            [
                QueryResultTypes.Kundorder,
                "Kundorder"
            ],
            [
                QueryResultTypes.Handelssortiment,
                "Handelssortiment"
            ],
            [
                QueryResultTypes.Virkeskopskontrakt,
                "Virkesköpskontrakt"
            ],
            [
                QueryResultTypes.VirkeskopskontraktFPV,
                "Skogsvårdsuppdrag"
            ],
            [
                QueryResultTypes.Produkt,
                "Produkt"
            ],
            [
                QueryResultTypes.Terminalorder,
                "Terminalorder"
            ],
            [
                QueryResultTypes.Leverantorsorder,
                "Leverantörsorder"
            ],
            [
                QueryResultTypes.Produktinstruktion,
                "Produktinstruktion"
            ]
        ]);
    }

    getTooltipText(row: ISearchResponseRow): string {
        if (row.type === QueryResultTypes.Modal) {
            return row.title;
        }

        return this.typeTooltipMap.get(row.type);
    }

    inputFormatter = (item: ISearchResponseRow): string => "";

    itemSelected($event): void {
        const transitionTo = (path: any[], parameters?: any) => {
            this.router.navigate(path, { queryParams: parameters });
        };

        this.searchElement.nativeElement.blur();

        const item = $event.item as ISearchResponseRow;
        switch (item.type) {
            case QueryResultTypes.Arbetsorder:
                transitionTo([
                    "planera",
                    item.id
                ]);
                break;
            case QueryResultTypes.VirkeskopskontraktFPV:
                transitionTo([
                    "skogsvardfpv/avtal",
                    item.metadata.avtalId
                ], { kontraktId: item.id });
                break;
            case QueryResultTypes.Virkeskopskontrakt:
                transitionTo([
                    "virkeskop/avtal",
                    item.metadata.avtalId
                ], { kontraktId: item.id });
                break;
            case QueryResultTypes.Handelssortiment:
                transitionTo([
                    "handelssortiment",
                    item.id
                ]);
                break;
            case QueryResultTypes.Kundorder:
                transitionTo([
                    "kundorder",
                    item.id
                ]);
                break;
            case QueryResultTypes.Leverantorsorder:
                transitionTo([
                    "leverantorsorder",
                    item.id
                ]);
                break;
            case QueryResultTypes.Terminalorder:
                transitionTo([
                    "terminalorder",
                    item.id
                ]);
                break;
            case QueryResultTypes.Produkt:
                transitionTo([
                    "produkt",
                    item.id
                ]);
                break;
            case QueryResultTypes.Produktinstruktion:
                transitionTo([
                    "produktinstruktioner",
                    item.naturalKey
                ]);
                break;
            case QueryResultTypes.Modal:
                this.openModal(item.title);
                break;
            default:
                break;
        }
    }

    keyEvent = (event: KeyboardEvent) => {
        if (event.ctrlKey && event.shiftKey && event.key === "F") {
            event.preventDefault();
            this.searchElement.nativeElement.focus();
        }
    };

    ngOnDestroy(): void {
        window.removeEventListener("keydown", this.keyEvent);
    }

    ngOnInit(): void {
        this.zone.runOutsideAngular(() => {
            window.addEventListener("keydown", this.keyEvent);
        });
    }

    onBlur = () => {
        // Clear input
        this.searchString = null;
        this.clearSrc.next();
        this.activeSearchResults.next([]);
    };

    onKeyDown = ($event: KeyboardEvent) => {
        if (!this.typeaheadInstance.isPopupOpen()) {
            return;
        }

        if ($event.key !== "ArrowDown" && $event.key !== "ArrowUp") {
            return;
        }

        asyncScheduler.schedule(() => {
            this.scrollIntoView();
        }, 200);
    };

    searchExtended = (): void => {
        this.extendedSearchSubject.next(this.searchString);
    };

    searchHandler = (text$: Observable<string>) => {
        const clear$ = this.clearSrc.pipe(
            map(() => "")
        );
        const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());

        const standardSearch$ = merge(debouncedText$, clear$).pipe(
            map(term => term.trim()),
            filter(term => term.length > 0),
            debounceTime(this.debounceTime),
            switchMap((term: string) => {
                this.searchComplete$.next(false);
                this.activeSearchResults.next([]);
                return this.searchService.search(term).pipe(tap(this.handleSearchResponse(term, false)));
            })
        );

        const extendedSearch$ = this.extendedSearchSubject.pipe(
            map(term => (term || "").trim()),
            filter(term => term.length > 0),
            switchMap((term: string) => {
                this.searchComplete$.next(false);
                this.activeSearchResults.next([]);
                return this.searchService.extendedSearch(term).pipe(tap(this.handleSearchResponse(term, true)));
            })
        );

        return merge(standardSearch$, extendedSearch$).pipe(
            tap(result => this.activeSearchResults.next(result))
        );
    };

    private handleSearchResponse(searchTerm: string, extendedSearch: boolean): PartialObserver<ISearchResponseRow[]> {
        return {
            complete: () => {
                this.searchComplete$.next(true);
            }
        };
    }

    private openModal(title: string) {
        switch (title) {
            case "Mina maskinlag":
                setTimeout(() => {
                    firstValueFrom(this.minaMaskinlagDialogService.minaMaskinlagModal());
                }, 0);
                break;
            case "Mina produktionsmallar":
                setTimeout(() => {
                    firstValueFrom(this.minaProduktionsmallarDialogService.minaProduktionsmallarModal());
                }, 0);
                break;
            case "Mina förkalkylsmallar":
                setTimeout(() => {
                    firstValueFrom(this.minaForkalkylsmallarDialogService.minaForkalkylsmallarModal());
                }, 0);
                break;
            default:
                break;
        }
    }

    private scrollIntoView(): void {
        const popup = document.getElementById(this.typeaheadInstance.popupId);
        const activeElements = popup.getElementsByClassName("active");

        if (activeElements.length < 1) {
            return;
        }

        const activeElement = activeElements.item(0) as HTMLElement;

        if (typeof (activeElement as any).scrollIntoViewIfNeeded === "function") {
            (activeElement as any).scrollIntoViewIfNeeded(true); // Not part of any specification. This is a proprietary, WebKit-specific method.
        } else {
            const offsetTop = activeElement.offsetTop;
            const offsetHeight = activeElement.offsetHeight;

            if (popup.scrollHeight <= popup.offsetHeight) {
                return;
            }

            if (offsetTop + offsetHeight * 2 > popup.scrollTop + popup.offsetHeight) {
                popup.scrollTop = offsetTop - popup.offsetHeight + offsetHeight * 2;
            } else if (offsetTop - offsetHeight < popup.scrollTop) {
                popup.scrollTop = offsetTop - offsetHeight;
            }
        }
    }
}
